/**
 * NCAA CARA Compliance Type Definitions
 *
 * Types for CARA (Countable Athletically Related Activities) compliance tracking.
 * Based on database schema from migration: 20251206000000_compliance_ncaa_cara_blackouts.sql
 */

/**
 * Season phase for determining CARA hour limits
 */
export type SeasonPhase = 'preseason' | 'in_season' | 'post_season' | 'off_season';

/**
 * Session requirement type - determines if session counts toward CARA
 */
export type RequirementType = 'required' | 'voluntary' | 'non_countable';

/**
 * NCAA division
 */
export type Division = 'D1' | 'D2' | 'D3' | 'NAIA' | 'NJCAA';

/**
 * Team gender - critical for NCAA CARA filtering (women's teams only)
 */
export type TeamGender = 'men' | 'women' | 'mixed';

/**
 * CARA session record - represents a single practice/activity session
 */
export interface CARASession {
  id: string;
  team_id: string;
  session_date: string; // ISO date string
  requirement_type: RequirementType;
  duration_minutes: number;
  notes: string | null;
  telemetry_session_id: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

/**
 * CARA policy - defines hour limits for a sport/division/season combination
 */
export interface CARAPolicy {
  id: string;
  sport: string;
  division: Division;
  season_phase: SeasonPhase;
  weekly_hour_limit: number;
  alert_threshold_percent: number; // Trigger warning at this % of limit
  created_at: string;
  updated_at: string;
}

/**
 * Weekly CARA summary - returned by get_cara_weekly_summary() database function
 */
export interface CARAWeeklySummary {
  total_hours: number;
  session_count: number;
  hour_limit: number;
  percent_used: number;
  is_over_limit: boolean;
  is_near_limit: boolean;
  current_season: SeasonPhase;
  is_ncaa_women: boolean;
}

/**
 * Extended weekly summary with date range for display
 */
export interface CARAWeeklySummaryWithDates extends CARAWeeklySummary {
  week_start: string; // ISO date string
  week_end: string; // ISO date string
}

/**
 * CARA history entry - aggregated weekly data
 */
export interface CARAWeeklyHistory {
  team_id: string;
  week_start: string; // ISO date string
  total_hours: number;
  session_count: number;
  sessions?: CARASession[]; // Optional detailed sessions for expanded view
  summary?: CARAWeeklySummary; // Optional computed summary
}

/**
 * Team interface with CARA-relevant fields
 */
export interface Team {
  id: string;
  name: string;
  sport: string;
  division: Division | null;
  gender: TeamGender | null;
  current_season: SeasonPhase;
  created_at: string;
  updated_at: string;
}

/**
 * Form data for logging a new CARA session
 */
export interface LogSessionFormData {
  session_date: Date;
  requirement_type: RequirementType;
  duration_hours: number;
  duration_minutes: number;
  notes?: string;
  telemetry_session_id?: string;
}

/**
 * Activity log entry for FERPA compliance
 * Updated for Phase 2: FERPA Activity Logging
 */
export interface ActivityLog {
  id: string;
  user_id: string;
  user_role: string | null; // Coach role (owner, coach, assistant_coach)
  action_type: string; // Action performed (VIEW_ROWER_JOURNEY, etc.)
  resource_type: string | null; // Resource type (athlete, session, team)
  resource_id: string | null; // Resource UUID
  metadata: Record<string, any> | null; // Flexible JSONB metadata (team_id, athlete_ids, etc.)
  ip_address: string | null; // Client IP address
  user_agent: string | null; // Browser/client info
  created_at: string; // Timestamp
}

/**
 * Activity log action types
 * Defines standard action types for FERPA compliance tracking
 */
export const ACTIVITY_LOG_ACTIONS = {
  VIEW_ROWER_JOURNEY: 'VIEW_ROWER_JOURNEY',
  VIEW_ROWER_PROFILE: 'VIEW_ROWER_PROFILE',
  VIEW_ROWER_CONCEPT2: 'VIEW_ROWER_CONCEPT2',
  VIEW_SESSION_DATA: 'VIEW_SESSION_DATA',
  VIEW_CARA_COMPLIANCE: 'VIEW_CARA_COMPLIANCE',
  EXPORT_SESSION_DATA: 'EXPORT_SESSION_DATA',
  VIEW_TEAM_ROSTER: 'VIEW_TEAM_ROSTER',
  SHARE_ROWER_DATA: 'SHARE_ROWER_DATA',
  ADMIN_SET_USER_ROLE: 'ADMIN_SET_USER_ROLE',
  ADMIN_UPDATE_ORGANIZATION: 'ADMIN_UPDATE_ORGANIZATION',
  ADMIN_APPROVE_ORGANIZATION_DOMAIN: 'ADMIN_APPROVE_ORGANIZATION_DOMAIN',
  ADMIN_DENY_ORGANIZATION_DOMAIN: 'ADMIN_DENY_ORGANIZATION_DOMAIN',
  ADMIN_SET_ORGANIZATION_DOMAIN_PENDING: 'ADMIN_SET_ORGANIZATION_DOMAIN_PENDING',
  ADMIN_DEACTIVATE_ORGANIZATION_DOMAIN: 'ADMIN_DEACTIVATE_ORGANIZATION_DOMAIN',
  ADMIN_UPDATE_ORGANIZATION_DOMAIN: 'ADMIN_UPDATE_ORGANIZATION_DOMAIN',
  ADMIN_REMOVE_ORGANIZATION_DOMAIN: 'ADMIN_REMOVE_ORGANIZATION_DOMAIN',
  ADMIN_ADD_ORGANIZATION_PROGRAM: 'ADMIN_ADD_ORGANIZATION_PROGRAM',
  ADMIN_REMOVE_ORGANIZATION_PROGRAM: 'ADMIN_REMOVE_ORGANIZATION_PROGRAM',
  ADMIN_ADD_ORGANIZATION_TEAM: 'ADMIN_ADD_ORGANIZATION_TEAM',
  ADMIN_REMOVE_ORGANIZATION_TEAM: 'ADMIN_REMOVE_ORGANIZATION_TEAM',
  ADMIN_UPDATE_ORGANIZATION_PROGRAM: 'ADMIN_UPDATE_ORGANIZATION_PROGRAM',
  ADMIN_UPDATE_ORGANIZATION_TEAM: 'ADMIN_UPDATE_ORGANIZATION_TEAM',
  ADMIN_LOG_DSR_REQUEST: 'ADMIN_LOG_DSR_REQUEST',
  ADMIN_UPDATE_DSR_REQUEST: 'ADMIN_UPDATE_DSR_REQUEST',
  ADMIN_CREATE_RECRUITMENT_PROGRAM: 'ADMIN_CREATE_RECRUITMENT_PROGRAM',
  ADMIN_UPDATE_RECRUITMENT_PROGRAM: 'ADMIN_UPDATE_RECRUITMENT_PROGRAM',
  ADMIN_PUBLISH_RECRUITMENT_PROGRAM: 'ADMIN_PUBLISH_RECRUITMENT_PROGRAM',
  ADMIN_HIDE_RECRUITMENT_PROGRAM: 'ADMIN_HIDE_RECRUITMENT_PROGRAM',
  ADMIN_DELETE_RECRUITMENT_PROGRAM: 'ADMIN_DELETE_RECRUITMENT_PROGRAM',
  ADMIN_MERGE_RECRUITMENT_PROGRAM: 'ADMIN_MERGE_RECRUITMENT_PROGRAM',
  ADMIN_SAVE_RECRUITMENT_COACH: 'ADMIN_SAVE_RECRUITMENT_COACH',
  ADMIN_DELETE_RECRUITMENT_COACH: 'ADMIN_DELETE_RECRUITMENT_COACH',
  ADMIN_UPDATE_RECRUITMENT_SETTINGS: 'ADMIN_UPDATE_RECRUITMENT_SETTINGS',
  DSR_FORM_INTAKE: 'DSR_FORM_INTAKE',
  MINOR_ONBOARDING_STARTED: 'MINOR_ONBOARDING_STARTED',
  PARENT_APPROVE_MINOR_CONSENT: 'PARENT_APPROVE_MINOR_CONSENT',
  MINOR_ROWER_COMPLETE_SIGNUP: 'MINOR_ROWER_COMPLETE_SIGNUP',
  PARENTAL_CONSENT_GRANTED: 'PARENTAL_CONSENT_GRANTED',
  PARENT_WITHDRAW_MINOR_CONSENT: 'PARENT_WITHDRAW_MINOR_CONSENT',
  PARENTAL_CONSENT_WITHDRAWN: 'PARENTAL_CONSENT_WITHDRAWN',
  REQUEST_ANONYMIZE: 'REQUEST_ANONYMIZE',
  CANCEL_ANONYMIZE: 'CANCEL_ANONYMIZE',
  GUARDIANSHIP_REVOKED_ATHLETE_DELETION: 'GUARDIANSHIP_REVOKED_ATHLETE_DELETION',
  RECONSENT_REQUEST_CREATED: 'RECONSENT_REQUEST_CREATED',
  PARENT_APPROVE_RECONSENT: 'PARENT_APPROVE_RECONSENT',
  ATHLETE_AGE_OF_MAJORITY: 'ATHLETE_AGE_OF_MAJORITY',
  PARENT_ACCOUNT_DELETE_DISPOSE: 'PARENT_ACCOUNT_DELETE_DISPOSE',
  PARENT_ACCOUNT_DELETE_CANCEL_RESTORE: 'PARENT_ACCOUNT_DELETE_CANCEL_RESTORE',
  PARENT_IDENTITY_SESSION_CREATED: 'PARENT_IDENTITY_SESSION_CREATED',
  PARENT_IDENTITY_VERIFIED: 'PARENT_IDENTITY_VERIFIED',
  PARENT_IDENTITY_REQUIRES_INPUT: 'PARENT_IDENTITY_REQUIRES_INPUT',
  PARENT_IDENTITY_CANCELED: 'PARENT_IDENTITY_CANCELED',
  UPDATE_PERSONAL_DATA: 'UPDATE_PERSONAL_DATA',
} as const;

export type ActivityLogAction = (typeof ACTIVITY_LOG_ACTIONS)[keyof typeof ACTIVITY_LOG_ACTIONS];

/** Human-readable labels for Activity Audit (and exports). */
export function getActivityLogActionLabel(actionType: string): string {
  switch (actionType) {
    case ACTIVITY_LOG_ACTIONS.VIEW_ROWER_JOURNEY:
      return 'Viewed rower journey';
    case ACTIVITY_LOG_ACTIONS.VIEW_ROWER_PROFILE:
      return 'Viewed rower profile';
    case ACTIVITY_LOG_ACTIONS.VIEW_ROWER_CONCEPT2:
      return 'Viewed rower Concept2 training';
    case ACTIVITY_LOG_ACTIONS.VIEW_SESSION_DATA:
      return 'Viewed training session';
    case ACTIVITY_LOG_ACTIONS.VIEW_CARA_COMPLIANCE:
      return 'Viewed CARA compliance';
    case ACTIVITY_LOG_ACTIONS.EXPORT_SESSION_DATA:
      return 'Exported session data';
    case ACTIVITY_LOG_ACTIONS.VIEW_TEAM_ROSTER:
      return 'Viewed team roster';
    case ACTIVITY_LOG_ACTIONS.SHARE_ROWER_DATA:
      return 'Shared data access';
    case ACTIVITY_LOG_ACTIONS.ADMIN_SET_USER_ROLE:
      return 'Updated platform user role';
    case ACTIVITY_LOG_ACTIONS.ADMIN_UPDATE_ORGANIZATION:
      return 'Updated organization profile';
    case ACTIVITY_LOG_ACTIONS.ADMIN_APPROVE_ORGANIZATION_DOMAIN:
      return 'Approved coach signup domain';
    case ACTIVITY_LOG_ACTIONS.ADMIN_DENY_ORGANIZATION_DOMAIN:
      return 'Denied coach signup domain';
    case ACTIVITY_LOG_ACTIONS.ADMIN_SET_ORGANIZATION_DOMAIN_PENDING:
      return 'Set coach signup domain to pending';
    case ACTIVITY_LOG_ACTIONS.ADMIN_DEACTIVATE_ORGANIZATION_DOMAIN:
      return 'Deactivated coach signup domain';
    case ACTIVITY_LOG_ACTIONS.ADMIN_UPDATE_ORGANIZATION_DOMAIN:
      return 'Updated organization domain link';
    case ACTIVITY_LOG_ACTIONS.ADMIN_REMOVE_ORGANIZATION_DOMAIN:
      return 'Removed domain from organization';
    case ACTIVITY_LOG_ACTIONS.ADMIN_ADD_ORGANIZATION_PROGRAM:
      return 'Added organization program';
    case ACTIVITY_LOG_ACTIONS.ADMIN_REMOVE_ORGANIZATION_PROGRAM:
      return 'Removed organization program';
    case ACTIVITY_LOG_ACTIONS.ADMIN_ADD_ORGANIZATION_TEAM:
    case 'ADMIN_ADD_ORGANIZATION_SQUAD':
      return 'Added catalog team';
    case ACTIVITY_LOG_ACTIONS.ADMIN_REMOVE_ORGANIZATION_TEAM:
    case 'ADMIN_REMOVE_ORGANIZATION_SQUAD':
      return 'Removed catalog team';
    case ACTIVITY_LOG_ACTIONS.ADMIN_UPDATE_ORGANIZATION_PROGRAM:
      return 'Updated organization program';
    case ACTIVITY_LOG_ACTIONS.ADMIN_UPDATE_ORGANIZATION_TEAM:
    case 'ADMIN_UPDATE_ORGANIZATION_SQUAD':
      return 'Updated catalog team';
    case ACTIVITY_LOG_ACTIONS.ADMIN_CREATE_RECRUITMENT_PROGRAM:
      return 'Created recruitment program';
    case ACTIVITY_LOG_ACTIONS.ADMIN_UPDATE_RECRUITMENT_PROGRAM:
      return 'Updated recruitment program';
    case ACTIVITY_LOG_ACTIONS.ADMIN_PUBLISH_RECRUITMENT_PROGRAM:
      return 'Published recruitment program';
    case ACTIVITY_LOG_ACTIONS.ADMIN_HIDE_RECRUITMENT_PROGRAM:
      return 'Hid recruitment program';
    case ACTIVITY_LOG_ACTIONS.ADMIN_DELETE_RECRUITMENT_PROGRAM:
      return 'Removed recruitment program';
    case ACTIVITY_LOG_ACTIONS.ADMIN_MERGE_RECRUITMENT_PROGRAM:
      return 'Merged duplicate recruitment program';
    case ACTIVITY_LOG_ACTIONS.ADMIN_SAVE_RECRUITMENT_COACH:
      return 'Saved recruitment program coach';
    case ACTIVITY_LOG_ACTIONS.ADMIN_DELETE_RECRUITMENT_COACH:
      return 'Removed recruitment program coach';
    case ACTIVITY_LOG_ACTIONS.ADMIN_UPDATE_RECRUITMENT_SETTINGS:
      return 'Updated recruitment catalog settings';
    case ACTIVITY_LOG_ACTIONS.ADMIN_LOG_DSR_REQUEST:
      return 'Logged data subject request (manual/email intake)';
    case ACTIVITY_LOG_ACTIONS.ADMIN_UPDATE_DSR_REQUEST:
      return 'Updated data subject request';
    case ACTIVITY_LOG_ACTIONS.DSR_FORM_INTAKE:
      return 'Submitted data subject request (public form)';
    case ACTIVITY_LOG_ACTIONS.MINOR_ONBOARDING_STARTED:
      return 'Started minor onboarding';
    case ACTIVITY_LOG_ACTIONS.PARENT_APPROVE_MINOR_CONSENT:
      return 'Parent approved minor consent';
    case ACTIVITY_LOG_ACTIONS.MINOR_ROWER_COMPLETE_SIGNUP:
      return 'Minor rower completed signup';
    case ACTIVITY_LOG_ACTIONS.PARENTAL_CONSENT_GRANTED:
      return 'Parental consent granted';
    case ACTIVITY_LOG_ACTIONS.PARENT_WITHDRAW_MINOR_CONSENT:
      return 'Parent withdrew minor consent';
    case ACTIVITY_LOG_ACTIONS.PARENTAL_CONSENT_WITHDRAWN:
      return 'Parental consent withdrawn';
    case ACTIVITY_LOG_ACTIONS.REQUEST_ANONYMIZE:
      return 'Requested account anonymization';
    case ACTIVITY_LOG_ACTIONS.CANCEL_ANONYMIZE:
      return 'Cancelled account anonymization';
    case ACTIVITY_LOG_ACTIONS.GUARDIANSHIP_REVOKED_ATHLETE_DELETION:
      return 'Guardianship revoked (athlete deletion)';
    case ACTIVITY_LOG_ACTIONS.RECONSENT_REQUEST_CREATED:
      return 'Athlete requested parent reconsent';
    case ACTIVITY_LOG_ACTIONS.PARENT_APPROVE_RECONSENT:
      return 'Parent approved reconsent';
    case ACTIVITY_LOG_ACTIONS.ATHLETE_AGE_OF_MAJORITY:
      return 'Athlete reached age of majority';
    case ACTIVITY_LOG_ACTIONS.PARENT_ACCOUNT_DELETE_DISPOSE:
      return 'Parent account delete disposed guardianship';
    case ACTIVITY_LOG_ACTIONS.PARENT_ACCOUNT_DELETE_CANCEL_RESTORE:
      return 'Restored guardianship after delete cancel';
    case ACTIVITY_LOG_ACTIONS.PARENT_IDENTITY_SESSION_CREATED:
      return 'Parent started Stripe Identity session';
    case ACTIVITY_LOG_ACTIONS.PARENT_IDENTITY_VERIFIED:
      return 'Parent Stripe Identity verified';
    case ACTIVITY_LOG_ACTIONS.PARENT_IDENTITY_REQUIRES_INPUT:
      return 'Parent Stripe Identity requires input';
    case ACTIVITY_LOG_ACTIONS.PARENT_IDENTITY_CANCELED:
      return 'Parent Stripe Identity canceled';
    case ACTIVITY_LOG_ACTIONS.UPDATE_PERSONAL_DATA:
      return 'Updated personal data';
    default:
      return actionType.replace(/_/g, ' ');
  }
}

/**
 * Season phase option for dropdown selector
 */
export interface SeasonPhaseOption {
  value: SeasonPhase;
  label: string;
  hourLimit: number;
  description: string;
  color: string;
}

/**
 * CARA status for visual indicators
 */
export type CARAStatus = 'safe' | 'warning' | 'violation' | 'not_applicable';

/**
 * Helper function to determine CARA status from summary
 */
export function getCARAStatus(summary: CARAWeeklySummary): CARAStatus {
  if (!summary.is_ncaa_women) {
    return 'not_applicable';
  }
  if (summary.is_over_limit) {
    return 'violation';
  }
  if (summary.is_near_limit) {
    return 'warning';
  }
  return 'safe';
}

/**
 * Helper function to get status color
 */
export function getStatusColor(status: CARAStatus): string {
  switch (status) {
    case 'safe':
      return '#16A34A'; // green-600
    case 'warning':
      return '#EAB308'; // yellow-500
    case 'violation':
      return '#DC2626'; // red-600
    case 'not_applicable':
      return '#9CA3AF'; // gray-400
  }
}

/**
 * Helper function to get requirement type color
 */
export function getRequirementTypeColor(type: RequirementType): string {
  switch (type) {
    case 'required':
      return '#DC2626'; // red-600 - counts toward CARA
    case 'voluntary':
      return '#16A34A'; // green-600 - does not count
    case 'non_countable':
      return '#9CA3AF'; // gray-400
  }
}

/**
 * Helper function to format season phase for display
 */
export function formatSeasonPhase(phase: SeasonPhase): string {
  switch (phase) {
    case 'preseason':
      return 'Preseason';
    case 'in_season':
      return 'In-Season';
    case 'post_season':
      return 'Post-Season';
    case 'off_season':
      return 'Off-Season';
  }
}

/**
 * Helper function to format requirement type for display
 */
export function formatRequirementType(type: RequirementType): string {
  switch (type) {
    case 'required':
      return 'Required';
    case 'voluntary':
      return 'Voluntary';
    case 'non_countable':
      return 'Non-Countable';
  }
}

/**
 * Season phase options for dropdown
 */
export const SEASON_PHASE_OPTIONS: SeasonPhaseOption[] = [
  {
    value: 'in_season',
    label: 'In-Season',
    hourLimit: 20,
    description: '20 hours per week',
    color: '#2563EB', // blue-600
  },
  {
    value: 'off_season',
    label: 'Off-Season',
    hourLimit: 8,
    description: '8 hours per week',
    color: '#6B7280', // gray-500
  },
  {
    value: 'preseason',
    label: 'Preseason',
    hourLimit: 20,
    description: '20 hours per week',
    color: '#F59E0B', // amber-500
  },
  {
    value: 'post_season',
    label: 'Post-Season',
    hourLimit: 8,
    description: '8 hours per week',
    color: '#7C3AED', // purple-600
  },
];

/**
 * Requirement type options for session logging
 */
export const REQUIREMENT_TYPE_OPTIONS: Array<{
  value: RequirementType;
  label: string;
  description: string;
}> = [
  {
    value: 'required',
    label: 'Required',
    description: 'Counts toward CARA weekly limit',
  },
  {
    value: 'voluntary',
    label: 'Voluntary',
    description: 'Athlete-initiated, does NOT count toward CARA',
  },
  {
    value: 'non_countable',
    label: 'Non-Countable',
    description: 'Film review, recruiting, other non-countable activities',
  },
];
